import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  created_at: string;
  role: string;
  clinic_id: string;
}

interface ProviderData {
  id: string;
  specialty: string | null;
  active: boolean;
  profile_id: string;
  clinic_id: string;
  profile: Profile;
}

export interface Provider {
  id: string;
  name: string;
  specialty: string;
  nextAvailable: string;
  rating: number;
  experience: string;
}

export function useProviders(clinicId: string) {
  return useQuery({
    queryKey: ['providers', clinicId],
    queryFn: async (): Promise<Provider[]> => {
      console.log('Starting provider query with clinic ID:', clinicId);
      if (!clinicId) {
        console.log('No clinic ID provided, returning empty array');
        return [];
      }

      // Get the current user's session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Current user session:', session?.user?.id);
      console.log('DEBUG: Session details:', {
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        userRole: session?.user?.user_metadata?.role,
        sessionValid: !!session
      });
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        return [];
      }

      // Skip clinic lookup - go directly to doctor profiles
      console.log('DEBUG: Skipping clinic lookup, proceeding directly to doctor profiles');

      // First, let's test a completely unfiltered query to see what we can access
      const { data: testAllProfiles, error: testError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, role, clinic_id');

      console.log('DEBUG: Test query - all profiles accessible:', {
        count: testAllProfiles?.length || 0,
        profiles: testAllProfiles?.slice(0, 3), // Show first 3 for debugging
        error: testError
      });

      // Use RPC function instead of direct table access to bypass RLS
      const { data: doctorProfiles, error: profileError } = await supabase
        .rpc('get_providers_for_booking', { 
          clinic_uuid: clinicId 
        });

      console.log('DEBUG: Doctor profiles RPC query details:');
      console.log('  - Clinic ID searched:', clinicId);
      console.log('  - RPC error:', profileError);
      console.log('  - Raw doctor profiles result:', doctorProfiles);
      console.log('  - Number of profiles found:', doctorProfiles?.length || 0);

      if (profileError) {
        console.error('Error fetching doctor profiles via RPC:', profileError);
        return [];
      }

      // Let's also check what profiles exist for this clinic regardless of role (for comparison)
      const { data: allProfiles, error: allError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, role, clinic_id')
        .eq('clinic_id', clinicId);

      console.log('DEBUG: All profiles for this clinic (direct query):');
      console.log('  - All profiles:', allProfiles);
      console.log('  - All profiles count:', allProfiles?.length || 0);
      console.log('  - Roles found:', allProfiles?.map(p => p.role).join(', ') || 'none');

      console.log('Found doctor profiles via RPC:', doctorProfiles);

      if (!doctorProfiles || doctorProfiles.length === 0) {
        console.log('No doctor profiles found for clinic');
        return [];
      }

      // Transform doctor profiles directly to Provider format
      const transformedData = doctorProfiles.map(doctor => ({
        id: doctor.id, // Use profile ID as provider ID
        name: `Dr. ${doctor.first_name} ${doctor.last_name}`,
        specialty: 'General Practice', // Default specialty
        nextAvailable: "Check calendar",
        rating: 5.0,
        experience: `${Math.max(1, Math.floor((Date.now() - new Date(doctor.created_at).getTime()) / (1000 * 60 * 60 * 24 * 365)))}+ years`
      }));

      console.log('Transformed providers data:', transformedData);
      return transformedData;
    },
    enabled: !!clinicId,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes
    gcTime: 1000 * 60 * 30 // Keep data in cache for 30 minutes
  });
}